<template>
  <v-card-text
    class="sourcery__comments-card"
    @dragleave.capture="dragEnd"
    @dragover.capture="dragOver">
    <div class="d-flex justify-space-between align-center">
      <ToggleResolvedComments
        :resource-id="resourceId"
        :resource-type="resourceType"
        class="ma-0 pa-0" />

      <v-btn
        :disabled="!totalUnreadComments"
        class="elevation-0"
        color="black"
        outlined
        @click="setReadAllResourceComments({
          resourceId,
          resourceType,
        })">
        MARK ALL AS READ
      </v-btn>
    </div>

    <FileUpload
      ref="upload"
      v-model="files"
      :multiple="false"
      :drop="true"
      :drop-directory="false" />

    <DragAndDropArea
      absolute
      height="100%"
      max-width="100%"
      :is-dialog="false"
      :is-drag="$refs.upload && $refs.upload.dropActive"
      :class="{ 'drag-drop-container__active': drag }" />

    <DragAndDropArea
      v-show="fileForPreview"
      :is-dialog="false"
      absolute
      max-width="100%"
      height="100%"
      upload-later
      :custom-metadata="{
        resourceId,
        resourceType,
        commentKind,
      }"
      :file="fileForPreview"
      @close="fileForPreview = null"
      @uploadFile="createCommentAttachment" />

    <Comments
      ref="containerForComments"
      :data="{
        comments,
        nextToken,
        nextTokenForUnread,
      }"
      :edited-row-id="editedRowId"
      :files-src.sync="filesSrc"
      :is-disabled-comments="isDisabledComments"
      :row-id="rowId"
      :resource-id="resourceId"
      :resource-type="resourceType"
      :scrolled-comment="scrolledComment"
      class="sourcery__comments-card--main"
      data-test="row_comments_comment_block"
      allow-resolve-comments
      @manageCellCommentResolved="manageCellCommentResolved"
      @getComments="getCommentsList"
      @editComment="editCommentAction"
      @deleteComment="deleteComment" />

    <CommentsForm
      ref="commentsForm"
      :resource-type="resourceType"
      :resource-id="resourceId"
      :mentioned-users.sync="allMentionUsers"
      :private.sync="privateMode"
      :edit-id.sync="editedRowId"
      :message.sync="message"
      :value-blur.sync="valueBlur"
      :is-disabled-comments="isDisabledComments"
      :focus-message.sync="focusMessage"
      @editComment="editComment"
      @onPaste="onPaste"
      @createComment="createComment" />
  </v-card-text>
</template>
<script>

import {
  orderBy, uniqBy,
} from 'lodash';
import {
  mapActions, mapMutations, mapState,
} from 'vuex';
import FileUpload from 'vue-upload-component';

import {
  COMMENT_KIND, COMMENT_PRIVACY, COMMENT_TYPE,
} from '@/constants/comments';

import AppCommentsForm from '@/components/App/AppComments/AppCommentsForm';
import AppCommentsList from '@/components/App/AppComments/AppCommentsList';
import DragAndDropArea from '@/components/App/AppDragAndDropArea';

import CommentsResources from '@/mixins/CommentsResources';
import GetCommenCreatorAvatar from '@/mixins/getCommentCreatorAvatar';
import PasteClipboard from '@/mixins/PasteClipboard';

import CommentsApi from '@/services/graphql/comments';
import { subscribeForAttachments } from '@/services/graphql/commentsSubscriptions';

import {
  getS3ImageUrl, putS3ImageUrl,
} from '@/utils/url';

export default {
  name: 'ModalBody',
  components: {
    CommentsForm: AppCommentsForm,
    Comments: AppCommentsList,
    FileUpload,
    DragAndDropArea,
    ToggleResolvedComments: () => import('@/components/App/AppComments/AppCommentsToggleResolved'),
  },
  mixins: [
    PasteClipboard,
    CommentsResources,
    GetCommenCreatorAvatar,
  ],
  props: {
    generalCommentsModal: {
      type: Boolean,
      default: false,
    },
    resourceId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      privateMode: null,
      allMentionUsers: [],
      nextToken: null,
      nextTokenForUnread: null,
      editedRowId: null,
      message: null,
      valueBlur: '',
      focusMessage: false,
      scrolledComment: null,
      fileForPreview: null,
      drag: false,
      files: [],
      filesSrc: {
      },
      rowId: null,
    };
  },
  computed: {
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapState('Comments', ['collectionCommentsListV2', 'projectCommentsListV2', 'newProjectCommentIdV2', 'newCollectionCommentIdV2']),
    ...mapState(['avatars',
      'role', 'commentsSubscriptions',
    ]),
    privacy() {
      return this.privateMode ? COMMENT_PRIVACY.PRIVATE : COMMENT_PRIVACY.PUBLIC;
    },
    commentKind() {
      if (this.$route.query.generalComments) {
        return COMMENT_KIND.GENERAL;
      }
      return COMMENT_KIND.GENERAL;
    },
    isDisabledComments() {
      return false;
    },
    comments: {
      get() {
        const { currentResource } = this;
        return this[currentResource.list];
      },
      set(val) {
        const { currentResource } = this;
        return this[currentResource.setMutation](val);
      },
    },
  },
  watch: {
    collaborators(val) {
      val.forEach((item) => {
        if (item.picture && !this.avatars[item.picture]) {
          this.getAvatar({
            key: item.picture,
            getAvatars: true,
            context: this,
          });
        }
      });
    },
    files(val) {
      const { file } = val[val.length - 1];
      this.fileForPreview = file;
    },
    newProjectCommentIdV2: {
      handler(val) {
        if (val) {
          this.scrollToComment({
            commentId: val,
          });
        }
      },
      deep: true,
    },
    newCollectionCommentIdV2: {
      handler(val) {
        if (val) {
          this.scrollToComment({
            commentId: val,
          });
        }
      },
      deep: true,
    },
    comments: {
      async handler(val) {
        val.forEach(comment => {
          if (comment?.attachments) {
            const thumbnail = comment.attachments[0]?.thumbnails;
            const fileId = thumbnail?.small?.key || thumbnail?.large?.key;
            if (fileId && !this.filesSrc.hasOwnProperty(fileId)) {
              this.getThumbnail(fileId);
            }
            this.getAvatarsForComments(comment);
          }
        });
      },
      deep: true,
      immediate: true,
    },
    generalCommentsModal: {
      handler(val) {
        if (val) {
          this.getCommentsList();
        }
      },
      immediate: true,
    },
  },
  created() {
    window.addEventListener('dragover', (e) => {
      e.preventDefault();
    }, false);
    window.addEventListener('drop', (e) => {
      e.preventDefault();
    }, false);
  },
  mounted() {
    const { resourceType, resourceId, commentKind } = this;
    this.subscribeComments({
      resourceId,
      resourceType,
      commentKind,
    });
    if (this.isCollectionType) {
      this.setPrivateMode({
        commentType: COMMENT_TYPE.TEXT,
        privacy: COMMENT_PRIVACY.PRIVATE,
      });
    }
  },
  beforeDestroy() {
    this.clearCommentForm();
    this.fileForPreview = null;
    this.setReadComments();
    this.comments = [];
    this.nextToken = null;
    this.nextTokenForUnread = null;
    this.removeSubscriptions([`${this.resourceType}_${this.resourceId}`]);
  },
  methods: {
    ...mapMutations('Comments', ['setCollectionCommentsV2', 'setProjectCommentsV2']),
    ...mapMutations(['spinner']),
    ...mapActions({
      setComments: 'Comments/setCommentV2',
      deleteCommentV2: 'Comments/deleteCommentV2',
      removeSubscriptions: 'ManageSubscriptions/removeSubscriptions',
      subscribeComments: 'Comments/subscribeCommentsV2',
      handleError: 'handleError',
      setSlantV2: 'Comments/setSlantV2',
      setCommentImage: 'Comments/setCommentImage',
      setReadAllResourceComments: 'Comments/setReadAllResourceComments',
    }),
    ...mapActions([
      'handleError',
    ]),
    async getThumbnail(id) {
      try {
        const url = await getS3ImageUrl(id, {
          customPrefix: {
            public: '',
          }, level: 'public',
        });
        this.$set(this.filesSrc, id, url);
      } catch (err) {
        console.log('err with set image', err);
      }
    },
    setReadComments() {
      const {
        resourceType,
        resourceId,
      } = this;
      const readMessageIDs = this.comments.reduce((result, option) => {
        if (!option.read) {
          return [...result, option.commentId];
        }
        return result;
      }, []);
      if (!readMessageIDs.length) {
        return;
      }
      this.setSlantV2({
        resourceType,
        resourceId,
        readMessageIDs,
      });
    },
    async manageRowComment() {
      if (this.valueBlur) {
        await this.$refs.commentsForm.manageRowComment();
      }
    },
    async createCommentAttachment({ id, file, config }) {
      try {
        await this.manageRowComment();
        const { response } = await this.createComment({
          attachments: [ file?.name ],
          commentType: COMMENT_TYPE.ATTACHMENT,
        });
        const { attachments, commentId } = response;
        if (!attachments[0]?.id) {
          throw new Error('Empty attachment');
        }
        const attachmentId = attachments[0]?.id;
        await putS3ImageUrl(id, file, {
          ...config,
          metadata: {
            ...config.metadata,
            commentId,
            attachmentId,
            filename: file?.name,
          },
        });
        subscribeForAttachments({
          commentId,
          attachmentId,
        }, ({ data }) => {
          this.setCommentImage({
            commentId,
            attachment: data.response,
            resourceType: this.resourceType,
          });
        });
        this.fileForPreview = null;
      } catch (e) {
        this.handleError(e);
      }
    },
    async scrollToComment({ commentId: id, mentionedComment = false }) {
      try {
        await this.$nextTick();
        const commentsRows = this.$refs.containerForComments.$refs.commentsRows;
        if (!commentsRows) return;
        const comment = commentsRows.find(({ id: commentId = null } = {
        }) => {
          if (!commentId) return false;
          return commentId === `commentId_${id}`;
        });
        if (!comment) return;
        comment.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        if (mentionedComment && comment) {
          this.scrolledComment = id;
        }
      } catch (e) {
        console.log(e);
      }
    },
    dragEnd() {
      this.drag = false;
    },
    dragOver() {
      this.drag = true;
    },
    manageCellCommentResolved(comment) {
      this.spinner(true);
      this.editComment({
        ...comment,
        commentResolved: !comment.commentResolved,
      });
    },
    async createComment({ mentioned, message, attachments, commentType = COMMENT_TYPE.TEXT, resolve }) {
      try {
        const { resourceType, resourceId, activeWorkspaceId: workspaceId, commentKind, privacy } = this;
        const { data } = await CommentsApi.createComment({
          commentKind,
          privacy,
          resourceType,
          resourceId,
          workspaceId,
          message,
          commentType,
          mentioned,
          attachments,
        });
        this.clearCommentForm();
        this.setComments(data.response);
        return data;
      } catch (e) {
        console.log(e);
      } finally {
        if (resolve) {
          this.spinner(false);
          resolve();
        }
      }
    },
    async getCommentsList({ scanIndexForward = false } = {
    }) {
      try {
        const { activeWorkspaceId: workspaceId, resourceType, commentKind, resourceId, nextToken, nextTokenForUnread, mode } = this;
        const { data } = await CommentsApi.listComments({
          workspaceId,
          resourceType,
          resourceId,
          commentKind,
          scanIndexForward,
          limit: 1000,
          nextToken: scanIndexForward ? nextTokenForUnread : nextToken,
        });
        if (!data) {
          return;
        }
        const response = data.response;
        let commentsForSorting = [...this.comments, ...response.comments];
        commentsForSorting = uniqBy(commentsForSorting, 'commentId');
        this.comments = orderBy(commentsForSorting,
          ['createdDate'], ['asc']);
        if (mode) {
          this.privateMode = mode;
        }
        this.comments.forEach(comment => this.getAvatarsForComments(comment));
        if (scanIndexForward) {
          this.nextTokenForUnread = response.nextToken;
        } else {
          this.nextToken = response.nextToken;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async editComment({ commentId, message, mentioned, commentResolved = false }) {
      try {
        const { activeWorkspaceId: workspaceId, resourceType, commentKind, resourceId } = this;
        const { data } = await CommentsApi.editComment({
          workspaceId,
          resourceId,
          commentKind,
          commentId,
          resourceType,
          attributes: {
            message,
            ...mentioned && {
              mentioned,
            },
            commentResolved,
          },
        });
        this.spinner(false);
        this.clearCommentForm();
        this.focusMessage = false;
        this.setComments(data.response);
      } catch (e) {
        console.log(e);
      }
    },
    /**
     * @param commentId
     * @param privacy
     * @returns {Promise<void>}
     */
    async deleteComment({ commentId, privacy }) {
      this.spinner(true);
      try {
        const { resourceId, resourceType, commentKind, activeWorkspaceId: workspaceId } = this;
        await CommentsApi.deleteComment({
          workspaceId,
          resourceId,
          commentId,
          privacy,
          resourceType,
          commentKind,
        });
        this.spinner(false);
        this.deleteCommentV2({
          commentId,
          resourceType,
        });
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    clearCommentForm() {
      this.$refs.commentsForm.clearCommentForm();
      if (this.isCollectionType) {
        this.setPrivateMode({
          commentType: COMMENT_TYPE.TEXT,
          privacy: COMMENT_PRIVACY.PRIVATE,
        });
      }
    },
    editCommentAction({ commentId, message, commentType, privacy, cellPointer, mentioned }) {
      this.focusMessage = true;
      this.message = message;
      this.editedRowId = commentId;
      this.localCellPointer = cellPointer;
      this.allMentionUsers = mentioned;
      this.setPrivateMode({
        commentType, privacy,
      });
    },
    setPrivateMode({ commentType, privacy }) {
      if (privacy === 'public') {
        this.privateMode = null;
      } else if (privacy === 'private' && commentType === 'text') {
        this.privateMode = privacy;
      } else {
        this.privateMode = commentType;
      }
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep.v-badge {
  &__summary-comments {
    .v-badge__wrapper {
      top: 19px;
      left: -128px;
    }
  }
}
.file-uploads {
  display: none;
}
</style>
